// @flow
import Properties from "../../../Properties";
import { getInit, postInit, graphQLInit } from "../../global/types/constants";
import type { DispatchType } from "../../global/types/redux";
import {
  BillingCompanyNode,
  BillingProductPartNode,
  BillingHistoryNode,
  BillingPartNode
} from "../../account/types/GraphQlNodes";
import { apiFailCallbackHandle } from "../../account/containers/ErrosDisplay";
import {
  GET_ALL_BILLING_COMPANY,
  ADMIN_GET_CURRENT_BILLING,
  ADMIN_GET_HISTORY_BILLING,
  GET_ALL_BILLING_PARTS,
  ADMIN_GET_COMPANY_PRICES,
  ADMIN_UPDATE_COMPANY_PRICE,
  ADMIN_UPLOAD_INVOICE_FILE
} from "../../global/actions/actionNames";

const root = Properties.api_root;

export const fetchCompanies = (): * => (
  dispatch: DispatchType
): DispatchType => {
  const graphQl = `
  {
    companies{${BillingCompanyNode}}
  }
  `;
  return fetch(`${root}/api/v2/price/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then(
      (json: *): * => {
        dispatch({
          type: GET_ALL_BILLING_COMPANY,
          companies: json.data.companies
        });
      }
    );
};

export const generateBill = (
  doneCallback: Function,
  failCallBack: Function,
  year?: string,
  month?: string
): * => (dispatch: DispatchType): DispatchType => {
  const date = month + year ? `(setYear: ${year}, setMonth: ${month})` : "";

  const graphQl = `
  mutation {
    generateLastBill ${date}{
      ok
      billings{
        company{${BillingCompanyNode}}
        message
        year
        month
        billing {
          total
          items{${BillingProductPartNode}}
        }
      }
    }
  }  
  `;
  return fetch(`${root}/api/v2/price/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then(
      (json: JSON): * => {
        if (json.errors) {
          failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
        } else {
          const result = json.data.generateLastBill;
          if (result.ok) {
            doneCallback && doneCallback(result.billings);
          }
        }
      }
    )
    .catch(
      (err: *): * => {
        failCallBack && failCallBack(apiFailCallbackHandle(err.message));
      }
    );
};

export const fetchCompanyBillings = (
  companyId: string,
  doneCall?: Function
): * => (dispatch: DispatchType): DispatchType => {
  const graphQl = `
  {
    companyCurrentBillings(id: "${companyId}"){
      total
      items{${BillingProductPartNode}}
    }
    companyHistoryBillings(id: "${companyId}"){
      ${BillingHistoryNode}
    }
    companyPrices(id: "${companyId}"){
      id
      uuid
      year
      month
      customerPrices{
        edges{
          node{
            id
            price
            part{
              ${BillingPartNode}
            }
          }
        }
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/price/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then(
      (json: *): * => {
        dispatch({
          type: ADMIN_GET_CURRENT_BILLING,
          companyId: companyId,
          current: json.data.companyCurrentBillings
        });

        dispatch({
          type: ADMIN_GET_HISTORY_BILLING,
          companyId: companyId,
          history: json.data.companyHistoryBillings
        });
        dispatch({
          type: ADMIN_GET_COMPANY_PRICES,
          companyId: companyId,
          products: json.data.companyPrices
        });

        doneCall && doneCall();
      }
    );
};

export const fetchAllParts = (doneCall?: Function): * => (
  dispatch: DispatchType
): DispatchType => {
  const graphQl = `
  {
    allParts{${BillingPartNode}}
  }
  `;
  return fetch(`${root}/api/v2/price/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then(
      (json: *): * => {
        dispatch({
          type: GET_ALL_BILLING_PARTS,
          parts: json.data.allParts
        });

        doneCall && doneCall(json.data.allParts);
      }
    );
};

export const CreatePrice = (
  companyId: string,
  year: number,
  month: number,
  parts: Array<*>,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const products = parts.map(
    item => `{partNo : "${item.partNo}", price : ${item.price}}`
  );
  const graphQl = `
  mutation{
    setCustomerPrice(
      companyId: "${companyId}",
      year: ${year},
      month: ${month},
      parts: [${products}]
    ){
      ok
      errors {
        ...on CompnayNotFound{
          message
          errorType
        }
        ...on NotBillingCompany{
          message
          errorType
        }
        ...on PartNotFound{
          message
          errorType
        }
        ...on PriceExist{
          message
          errorType
        }
      }
      product {
        id
        uuid
        year
        month
        customerPrices{
          edges{
            node{
              id
              price
              part{
                ${BillingPartNode}
              }
            }
          }
        }
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/price/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then(
      (json: JSON): * => {
        if (json.errors) {
          failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
        } else {
          const result = json.data.setCustomerPrice;
          if (result.ok) {
            dispatch({
              type: ADMIN_UPDATE_COMPANY_PRICE,
              companyId: companyId,
              product: result.product
            });
            doneCallback && doneCallback();
          } else {
            failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
          }
        }
      }
    )
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const updatePrice = (
  productId: string,
  companyId: string,
  parts: Array<*>,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const products = parts.map(
    item => `{partNo : "${item.partNo}", price : ${item.price}}`
  );
  const graphQl = `
  mutation{
    updateCustomerPrice(
      productId: "${productId}",
      parts: [${products}]
    ){
      ok
      errors {
        ...on ProductNotFound{
          message
          errorType
        }
        ...on PartNotFound{
          message
          errorType
        }
      }
      product {
        id
        uuid
        year
        month
        customerPrices{
          edges{
            node{
              id
              price
              part{
                ${BillingPartNode}
              }
            }
          }
        }
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/price/graphql`, graphQLInit(graphQl))
    .then((response: Response): * => response.json())
    .then(
      (json: JSON): * => {
        if (json.errors) {
          failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
        } else {
          const result = json.data.updateCustomerPrice;
          if (result.ok) {
            dispatch({
              type: ADMIN_UPDATE_COMPANY_PRICE,
              companyId: companyId,
              product: result.product
            });
            doneCallback && doneCallback();
          } else {
            failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
          }
        }
      }
    )
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export function uploadInvoicefile(
  companyId: string,
  billingId: string,
  billingUid: string,
  paidOn: string,
  file?: File = null,
  doneCall: Function,
  failBack: Function
): * {
  let postFormData = new FormData();
  postFormData.append("paid_on", paidOn);
  file && postFormData.append("invoice_file", file);
  return (dispatch: DispatchType) => {
    fetch(
      root + "/api/v2/price/billings/" + billingUid + "/upload",
      postInit(postFormData)
    )
      .then((response: Response): * => response.json())
      .then(
        (json: JSON): * => {
          if (json.errors) {
            failBack && failBack(apiFailCallbackHandle(json.errors, "billing"));
          } else {
            dispatch({
              type: ADMIN_UPLOAD_INVOICE_FILE,
              companyId: companyId,
              billingId: billingId,
              paidOn: json.billing.paid_on,
              invoice: json.billing.invoice
            });
            doneCall && doneCall();
          }
        }
      )
      .catch(err => {
        failBack && failBack(apiFailCallbackHandle(err.message, "billing"));
      });
  };
}

export function getInvoiceFileUrl(
  billingId: string,
  billingUid: string,
  doneCall: Function,
  failBack: Function
): * {
  return (dispatch: DispatchType) => {
    fetch(root + "/api/v2/price/billings/" + billingUid + "/file", getInit)
      .then((response: Response): * => response.json())
      .then(
        (json: JSON): * => {
          if (json.errors) {
            failBack && failBack(apiFailCallbackHandle(json.errors, "billing"));
          } else {
            doneCall && doneCall(json.url);
          }
        }
      )
      .catch(err => {
        failBack && failBack(apiFailCallbackHandle(err.message, "billing"));
      });
  };
}
